import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  NonMemberCodeRedeemRequest,
  NonMemberCodeRedeemResponse,
} from "@/features/wallet/api/dto";

interface RedeemArgs {
  body: NonMemberCodeRedeemRequest;
  /** Clé d'idempotence (OBLIGATOIRE §4.7). Nouvelle clé par tentative métier. */
  idempotencyKey: string;
}

/**
 * Encaisse intégralement un code Non-Member
 * (`POST /NonMemberCodes/redeem`, guide v2.4 §6.3).
 *
 * Transaction `AGENT_CASH_OUT` depuis le portefeuille du bénéficiaire,
 * commission agent provisionnée. **Idempotency-Key OBLIGATOIRE** (§4.7).
 * Anti double encaissement par verrou optimiste (422 au second).
 * Codes : 404 code inconnu ; 422 expiré/annulé/bloqué/téléphone
 * incohérent/déjà encaissé.
 */
export const useRedeemNonMemberCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "nonMemberCodes", "redeem"],
    mutationFn: ({ body, idempotencyKey }: RedeemArgs) =>
      mutateData({
        url: walletEndpoints.nonMemberCodes.redeem,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
        idempotencyKey,
      }) as Promise<NonMemberCodeRedeemResponse>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, walletTransactionCacheKeys()),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRedeemNonMemberCode failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
